import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/fmt";
import { StickyNote, PhoneCall, MessageSquare, Mail, FileText, ArrowRightLeft, CheckCircle2, Video, Circle, Undo2 } from "lucide-react";

const ICON = {
  note: StickyNote, call: PhoneCall, message: MessageSquare, email_sent: Mail, gmail: Mail,
  form_submission: FileText, stage_change: ArrowRightLeft, task_completed: CheckCircle2, task_edited: CheckCircle2, task_deleted: CheckCircle2,
  meeting_booked: Video, meeting_status: Video,
};

export function describe(a) {
  const d = a.data || {};
  switch (a.kind) {
    case "note": return { title: "Note", body: a.text };
    case "call": return { title: `Call · ${d.outcome || ""}${d.duration_min ? ` · ${d.duration_min}m` : ""}`, body: a.text };
    case "message": return { title: `Message · ${d.channel || ""}`, body: a.text };
    case "email_sent": return { title: "Email sent", body: a.text };
    case "gmail": return { title: a.subject || "(no subject)", body: `${a.from || ""}\n${a.snippet || ""}` };
    case "form_submission": return {
      title: `Submitted “${d.form_title || d.form_slug}”`,
      body: Object.values(d.answers || {}).filter((x) => x.type !== "calendar_meet").map((x) => `${x.label}: ${Array.isArray(x.value) ? x.value.join(", ") : x.value}`).join("\n"),
    };
    case "stage_change": return { title: `Stage: ${d.from ? `${d.from} → ` : ""}${d.to}${d.reason ? ` (${d.reason})` : ""}` };
    case "task_completed": return { title: `Task done · ${d.outcome}`, body: d.note };
    case "task_edited": return { title: `Task edited · ${d.label || ""}`, body: Object.entries(d.changes || {}).map(([k, v]) => `${k}: ${k === "due" ? fmtDateTime(v.from) : v.from || "—"} → ${k === "due" ? fmtDateTime(v.to) : v.to || "—"}`).join("\n") };
    case "task_deleted": return { title: `Task deleted · ${d.label || ""}`, body: d.due ? `was due ${fmtDateTime(d.due)}` : "" };
    case "approval_requested": return { title: `Approval requested · ${d.label}`, body: `${d.summary || ""}\nReason: ${d.reason || ""}` };
    case "approval_approved": return { title: `Approved · ${d.label}`, body: `${d.summary || ""}\nRequested by ${d.requested_by}${d.note ? ` · "${d.note}"` : ""}` };
    case "approval_rejected": return { title: `Rejected · ${d.label}`, body: `${d.summary || ""}\nRequested by ${d.requested_by}${d.note ? ` · "${d.note}"` : ""}` };
    case "approval_failed": return { title: `Approved but not applied · ${d.label}`, body: d.summary || "" };
    case "meeting_booked": return { title: d.ok ? "Meet booked" : "Meet requested (calendar not confirmed)", body: `${fmtDateTime(d.start)} · ${d.organizer || ""}${d.meet_url ? `\n${d.meet_url}` : ""}${!d.ok && d.error ? `\n${d.error}` : ""}` };
    case "meeting_status": return { title: `Meeting marked ${String(d.status).replace("_", "-")}` };
    default: return { title: a.kind, body: a.text };
  }
}

function UndoButton({ a, onChanged }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!window.confirm(`Undo outcome "${a.data.sop_outcome}"? The stage, fields and SOP step go back to how they were.`)) return;
    setBusy(true);
    try {
      await api.post(`/crm/sop/leads/${a.lead_id}/undo/${a._id}`);
      toast.success("Outcome undone. SOP step reopened");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not undo");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} data-testid={`timeline-undo-${a._id}`}
      className="mt-1.5 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[var(--border-strong)] text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--panel-2)] transition-colors disabled:opacity-50">
      <Undo2 size={12} />{busy ? "Undoing..." : "Undo outcome"}
    </button>
  );
}

export default function Timeline({ activity, emails, onChanged }) {
  const lastUndo = activity.filter((a) => a.data?.undo && !a.data?.undone).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
  const items = [
    ...activity,
    ...(emails || []).map((e) => ({ ...e, _id: `g-${e.id}`, kind: "gmail", created_at: e.date ? new Date(e.date).toISOString() : null })),
  ].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  if (!items.length) return <div className="text-sm text-[var(--text-faint)] py-6">No activity yet.</div>;
  return (
    <ol className="relative border-l border-[var(--border-strong)] ml-3 space-y-5" data-testid="lead-timeline">
      {items.map((a) => {
        const I = ICON[a.kind] || Circle;
        const { title, body } = describe(a);
        return (
          <li key={a._id} className="ml-6" data-testid={`timeline-${a.kind}`}>
            <span className="absolute -left-[13px] w-[26px] h-[26px] rounded-full bg-[var(--panel-2)] border border-[var(--border-strong)] flex items-center justify-center">
              <I size={12} className="text-[var(--text-dim)]" />
            </span>
            <div className="flex items-baseline gap-3">
              <span className="text-sm font-medium text-[var(--text)]">{title}</span>
              <span className="text-xs text-[var(--text-faint)] ml-auto whitespace-nowrap">{fmtDateTime(a.created_at)}</span>
            </div>
            {body && <div className="text-[13px] leading-relaxed text-[var(--text-dim)] whitespace-pre-line mt-1 break-words">{body}</div>}
            {a.actor && a.kind !== "gmail" && <div className="text-xs text-[var(--text-faint)] mt-1">{a.actor}</div>}
            {a.data?.undone && <div className="text-xs text-[var(--text-faint)] mt-1 italic" data-testid={`timeline-undone-${a._id}`}>Undone by {a.data.undone_by}</div>}
            {lastUndo && lastUndo._id === a._id && <UndoButton a={a} onChanged={onChanged} />}
          </li>
        );
      })}
    </ol>
  );
}
